import {
  Barcode,
  BarcodeGenerationType,
  BarcodeStatus,
  PrintScanBarcodesState,
  StoreBarcodeList,
} from '../types/printScanBarcode';
import {
  DeliveryLocationStatus,
  SetDeliveryLocationStatusOrdersResponse,
} from '@buncha/batchDetails/types';
import {
  GENERATE_BARCODES_DATA_FOR_ORDER,
  GENERATE_BARCODES_FILE,
  SET_DELIVERY_LOCATION_STATUS_ORDERS,
  UPDATE_BARCODE_STATUS,
} from '@buncha/config/graphQL/documentNode/mutation';
import {
  GenerateBarcodesDataForOrderResponse,
  GenerateBarcodesFileResponse,
  GetBarcodeOrderInfoReq,
  GetBarcodesForOrdersResponse,
  OrderInfo,
  UpdateBarcodeStatusResponse,
} from '@buncha/config/graphQL/type';
import {GroupedOrder, Order} from '../types/vanLoadAt';
import {PayloadAction, createSlice} from '@reduxjs/toolkit';
import {
  PrintScanBarcodesContext,
  initialState,
} from '../context/printScanBarcodes';
import {useCallback, useContext, useReducer, useState} from 'react';

import {Analytics} from '@buncha/config/analytics/analytics';
import {CommonScreens} from '@buncha/common/navigation/types';
import {GET_BARCODES_FOR_ORDERS} from '@buncha/config/graphQL/documentNode/queries';
import {MainScreens} from '@buncha/navigation/types';
import {PrinterNativeModule} from '../components/PrinterNativeModule';
import {ToastType} from '@buncha/components/composites/notification/type';
import {UserTypes} from '@buncha/onBoarding/types';
import {gqlService} from '@buncha/config/graphQL';
import {navigation} from '@buncha/config/navigation';
import {useAppSelector} from '@buncha/config/redux/hooks';
import {useToastMessage} from '@buncha/components/composites/notification';

const reducer = {
  setBarcodesPrinting: function (
    state: PrintScanBarcodesState,
    action: PayloadAction<boolean>,
  ) {
    state.barcodesPrinting = action.payload;
  },
  setBarcodesList: function (
    state: PrintScanBarcodesState,
    action: PayloadAction<Barcode[]>,
  ) {
    state.barcodesList = action.payload;
  },
  setBarcodeType: function (
    state: PrintScanBarcodesState,
    action: PayloadAction<BarcodeGenerationType>,
  ) {
    state.barcodeType = action.payload;
  },
  setStoreBarcodeList: function (
    state: PrintScanBarcodesState,
    action: PayloadAction<GroupedOrder[]>,
  ) {
    let storeList: StoreBarcodeList[] = [];
    if (action.payload.length) {
      const orders = action.payload[0]?.orders ?? [];
      orders.forEach((order: Order, index: number) => {
        const store = order?.run?.store;
        storeList.push({
          index: index + 1,
          storeName: store?.name,
          storeLogo: store?.storeLogoUrl,
          barcodeCount: order?.bagsUsed,
        });
      });
      state.storeBarcodeList = storeList;
    }
  },
};

const slice = createSlice({
  initialState,
  name: 'PrintScanBarcodeContext',
  reducers: reducer,
});

export function usePrintScanBarcodesReducer() {
  const reducerInfo = useReducer(slice.reducer, initialState);
  return reducerInfo;
}

export function usePrintScanBarcodesContext() {
  return useContext(PrintScanBarcodesContext);
}

export const PrintScanBarcodeAction = slice.actions;

export function useGetBarcodesForOrders() {
  const [loading, setLoading] = useState(false);
  const {dispatch} = usePrintScanBarcodesContext();
  const [barcodes, setBarcodes] = useState<Barcode[]>([]);
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const getBarcodesForOrders = useCallback(
    async (params: GetBarcodeOrderInfoReq) => {
      try {
        setLoading(true);
        const response = await gqlService?.query<GetBarcodesForOrdersResponse>({
          query: GET_BARCODES_FOR_ORDERS,
          fetchPolicy: 'network-only',
          variables: {
            orderInfo: params.orderInfo,
          },
        });
        const barcodesList = response?.data?.getBarcodesForOrders ?? [];
        let dummyList: any;
        if (barcodesList.length)
          if (
            barcodesList[0]?.generationType === BarcodeGenerationType.prePrinted
          )
            dummyList = generateDummyBarcodeList(
              barcodesList.length,
              dispatch,
              barcodesList,
            );
          else if (dispatch)
            dispatch(PrintScanBarcodeAction?.setBarcodesList(barcodesList));

        if (
          barcodesList[0]?.generationType === BarcodeGenerationType.prePrinted
        )
          setBarcodes(dummyList);
        else setBarcodes(barcodesList);
        setLoading(false);
        return barcodesList;
      } catch (error: any) {
        setLoading(false);
        showErrorToast(error?.message);
      }
    },
    [dispatch, showErrorToast],
  );
  return {loading, getBarcodesForOrders, barcodes};
}

export function useGenerateBarcodesDataForOrder() {
  const [printingLabel, setPrintingLabel] = useState(false);
  const {dispatch} = usePrintScanBarcodesContext();
  const [generatedBarcodes, setGeneratedBarcodes] = useState<Barcode[]>([]);
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const {generateBarcodesFile} = useGenerateBarcodesFile();
  const [showSuccessToast] = useToastMessage(ToastType.Success);

  const rnPrinter = PrinterNativeModule.RNPrinter;
  const BLEDevice = useAppSelector(gbState => gbState.driver.BLEDevice);
  const generateBarcodesDataForOrder = useCallback(
    async (
      orderIds: number[],
      printBarcodes: boolean,
      callBack?: Function,
      runId?: number,
      b2BOrderIds?: number[],
    ) => {
      const ids = orderIds.length > 0 ? orderIds : b2BOrderIds;
      try {
        if (dispatch)
          dispatch(PrintScanBarcodeAction.setBarcodesPrinting(true));
        setPrintingLabel(true);
        const response =
          await gqlService?.mutation<GenerateBarcodesDataForOrderResponse>({
            mutation: GENERATE_BARCODES_DATA_FOR_ORDER,
            fetchPolicy: 'network-only',
            variables: {
              orderIds: ids,
              runId: runId,
            },
          });
        const generatedCodes =
          response?.data?.generateBarcodesDataForOrder ?? [];
        setGeneratedBarcodes(generatedCodes);
        if (dispatch) {
          dispatch(PrintScanBarcodeAction?.setBarcodesList(generatedCodes));
          dispatch(PrintScanBarcodeAction.setBarcodesPrinting(false));
        }
        if (printBarcodes) {
          const generatedBarcode =
            (await generateBarcodesFile(orderIds, b2BOrderIds || [])) ??
            undefined;
          if (generatedBarcode && BLEDevice?.address) {
            await rnPrinter.PrintLabel(generatedBarcode, BLEDevice?.address);
            showSuccessToast('Printing completed successfully');
            Analytics.eventWithProps('Printer scan', {
              status: 'success',
            });
          }
        }
        if (callBack) callBack(generatedCodes);
        setPrintingLabel(false);
      } catch (error: any) {
        if (dispatch)
          dispatch(PrintScanBarcodeAction.setBarcodesPrinting(false));
        setPrintingLabel(false);
        showErrorToast(error?.message);
        Analytics.eventWithProps('Printer scan', {
          status: 'failed',
          error: error?.message ?? '',
        });
        if (error?.message.includes('SDK'))
          navigation.navigate(MainScreens.Common, {
            screen: CommonScreens.PrinterSetting,
          });
      }
    },
    [
      BLEDevice?.address,
      dispatch,
      generateBarcodesFile,
      rnPrinter,
      showErrorToast,
      showSuccessToast,
    ],
  );
  return {printingLabel, generateBarcodesDataForOrder, generatedBarcodes};
}

export function useUpdateBarcodeStatus(generationType: BarcodeGenerationType) {
  const [loading, setLoading] = useState(false);
  const [errorMSG, setErrorMSG] = useState<String | undefined>(undefined);
  const {getBarcodesForOrders, barcodes} = useGetBarcodesForOrders();
  const [showErrorToast] = useToastMessage(ToastType.Error);

  const updateBarcodeStatus = useCallback(
    async (
      code: string,
      status: BarcodeStatus,
      orderIds: number[],
      callback: (scannedCount: number) => void,
      runId: number,
      orderInfo: OrderInfo[],
      // eslint-disable-next-line max-params
    ) => {
      try {
        setErrorMSG('');
        setLoading(true);
        const res = await gqlService?.mutation<UpdateBarcodeStatusResponse>({
          mutation: UPDATE_BARCODE_STATUS,
          fetchPolicy: 'network-only',
          variables: {
            code: code,
            status: status,
            orderIds: orderIds,
            generationType: generationType,
            runId: runId,
          },
        });
        if (res?.data?.updateBarcodeStatus) {
          const updatedCode = res?.data?.updateBarcodeStatus?.code;
          let barcodeList;
          if (updatedCode)
            barcodeList = await getBarcodesForOrders({orderInfo: orderInfo});

          const scannedBoxCount =
            barcodeList?.filter(
              barcode => barcode.status === BarcodeStatus.scanned,
            ).length || 0;
          callback(scannedBoxCount);
        }
        setLoading(false);
      } catch (error: any) {
        setLoading(false);
        setErrorMSG(error?.message);
        showErrorToast(error?.message);
        throw error;
      }
    },

    [generationType, getBarcodesForOrders, showErrorToast],
  );
  return {loading, updateBarcodeStatus, errorMSG, barcodes};
}

export function useGenerateBarcodesFile() {
  const [loading, setLoading] = useState(false);
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const generateBarcodesFile = useCallback(
    async (orderIds: number[], b2bOrderIds: number[]) => {
      try {
        setLoading(true);
        const response =
          await gqlService?.mutation<GenerateBarcodesFileResponse>({
            mutation: GENERATE_BARCODES_FILE,
            fetchPolicy: 'network-only',
            variables: {
              orderIds: orderIds || [],
              b2bOrderIds: b2bOrderIds || [],
            },
          });
        const decodedData = response?.data?.generateBarcodesFile ?? '';
        setLoading(false);
        return decodedData;
      } catch (error: any) {
        setLoading(false);
        showErrorToast(error?.message);
      }
    },
    [showErrorToast],
  );
  return {loading, generateBarcodesFile};
}

export const generateDummyBarcodeList = (
  numberOfBarcodes: number,
  dispatch: any,
  barcodeList?: Barcode[],
) => {
  let dummyList = [];
  let barcodeCount = numberOfBarcodes || barcodeList?.length || 0;
  for (let i = 0; i < barcodeCount; i++)
    if (barcodeList && barcodeList[i].status === BarcodeStatus.scanned)
      dummyList.push(barcodeList[i]);
    else
      dummyList.push({
        code: 'XXXXXXXXXXXXX',
        createdAt: Date.now(),
        generationType: BarcodeGenerationType.prePrinted,
        id: 0,
        order: {
          id: 0,
        },
        orderId: 0,
        position: 0,
        status: BarcodeStatus.generated,
      });
  if (dispatch) {
    dispatch(PrintScanBarcodeAction?.setBarcodesList(dummyList));
    dispatch(
      PrintScanBarcodeAction?.setBarcodeType(BarcodeGenerationType.prePrinted),
    );
  }
  return dummyList;
};

export function useSetDeliveryLocationStatusForDriver() {
  const [showMessage] = useToastMessage(ToastType.Error);
  const [statusLoading, setStatusLoading] = useState(false);

  const setDeliveryLocationStatusForDriver = useCallback(
    async (
      runId: number,
      status: DeliveryLocationStatus,
      orderIds: number[],
      callBack: () => void,
    ) => {
      setStatusLoading(true);
      try {
        const response =
          await gqlService?.mutation<SetDeliveryLocationStatusOrdersResponse>({
            mutation: SET_DELIVERY_LOCATION_STATUS_ORDERS,
            variables: {
              runId: runId,
              status: status,
              orderIds: orderIds,
              userType: UserTypes.driver,
            },
            fetchPolicy: 'network-only',
          });
        if (response?.data?.setDeliveryLocationStatusOfAssignedOrders)
          callBack();
        setStatusLoading(false);
      } catch (error: any) {
        showMessage(error.message);
        setStatusLoading(false);
      }
    },
    [showMessage],
  );

  return {setDeliveryLocationStatusForDriver, statusLoading};
}

export function useChangeToPreprintedFlow() {
  const [loading, setLoading] = useState(false);
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const changeToPreprintedFlow = useCallback(
    async (orderIds: number[], runId: number) => {
      try {
        setLoading(true);
        await gqlService?.mutation<UpdateBarcodeStatusResponse>({
          mutation: UPDATE_BARCODE_STATUS,
          fetchPolicy: 'network-only',
          variables: {
            orderIds: orderIds,
            updateAll: true,
            generationType: BarcodeGenerationType.prePrinted,
            runId: runId,
          },
        });
        setLoading(false);
        return true;
      } catch (error: any) {
        setLoading(false);
        showErrorToast(error?.message);
      }
    },
    [showErrorToast],
  );
  return {loading, changeToPreprintedFlow};
}
